/**
 * Answering calls in this browser on every page (event-calls S3b). A person turns it on once, on an event's Calls page;
 * this browser remembers that for them and connects again on each visit until they turn it off. A call that rings shows a
 * banner with Answer and Decline (a bar along the foot of a phone or tablet screen, a card in the corner of a wide one)
 * and a ring. A dropped connection is retried three times, then a notice says calls can't ring here, with Try again and Turn off.
 * A call out from this browser (S6b) is placed by the server, which rings this browser back: that one leg, and only it, is
 * answered here on its own. A call on in this browser (ours out from the moment it is placed, one in once answered) shows
 * the call panel (CallPanel, call-center B2) where the shell puts CallDock; a call with our own record stays on it after it
 * ends, until Close, so its notes can be finished.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMatch } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { InlineReport } from '@/components/ui/ReportControl';
import { phone } from '@/lib/format';
import { BookingDialog } from './BookingDialog';
import { CallPanel, type PanelCall } from './CallPanel';
import { callFrom } from './data';
import { leavePhone, startPhone, type Phone, type PhoneCall, type PhoneState } from './phone';
import { startRing, unlockRing, unlockRingOnTouch } from './ring';

const RETRY_SECONDS = [10, 30, 90];
/** How long after asking for a call out this browser waits for the server's leg to ring it. */
const OUT_WAIT = 30_000;

interface Calls {
  state: PhoneState;
  error: string | null;
  busy: boolean;
  turnOn(): void;
  turnOff(): Promise<void>;
  /** true while this browser is on a call, or placing one. */
  onCall: boolean;
  /** Place a call out from this browser: `place` asks the server, which answers with the call's id and rings this browser
   * with it; that leg is answered here without a click. false = this browser is busy, or the server placed nothing. */
  callOut(number: string, place: () => Promise<string | null>): Promise<boolean>;
}
const CallsContext = createContext<Calls | null>(null);

export function useCalls(): Calls {
  const c = useContext(CallsContext);
  if (!c) throw new Error('useCalls outside CallsProvider');
  return c;
}

// kept per person, like the browser's own key (phone.ts): it is what makes calls ring here, not a display preference
const wantKey = (userId: string) => `rmp2.phone.on.${userId}`;
function wanted(userId: string): boolean {
  try {
    return localStorage.getItem(wantKey(userId)) === '1';
  } catch {
    return false;
  }
}
function want(userId: string, on: boolean) {
  try {
    if (on) localStorage.setItem(wantKey(userId), '1');
    else localStorage.removeItem(wantKey(userId));
  } catch { /* not remembered */ }
}

interface Ringing { call: PhoneCall; answering: boolean; talking: boolean; out?: { number: string; id: string } }
/** A call out being placed: legs that ring with one of our ids wait here until the server says which one is ours. */
interface Placing { number: string; id: string | null; parked: PhoneCall[]; timer: number }

export function CallsProvider({ userId, children }: { userId: string; children: ReactNode }) {
  const [state, setState] = useState<PhoneState>('off');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ringing, setRinging] = useState<Ringing | null>(null);
  const [placing, setPlacing] = useState(false);
  /** The call on the panel after it ended, kept until Close so its notes can be finished. */
  const [kept, setKept] = useState<PanelCall | null>(null);
  /** The call a new booking is open for (its id), from the panel. */
  const [booking, setBooking] = useState<string | null>(null);
  const placingRef = useRef<Placing | null>(null);
  // the notice waits until the retries are spent (or the server refused), so a short drop never shows it
  const [gaveUp, setGaveUp] = useState(false);
  const phoneRef = useRef<Phone | null>(null);
  const callRef = useRef<PhoneCall | null>(null);
  const retry = useRef({ tries: 0, timer: 0 });
  const audioRef = useRef<HTMLAudioElement>(null);
  // the Calls page shows the same failure on its own card
  const onCallsPage = useMatch('/events/:eventId/calls') !== null;

  /** A call on the bar: one that rings (Answer, Decline), or our own call out, answered at once. */
  const show = useCallback((call: PhoneCall, out?: Ringing['out']) => {
    // one call at a time: a second one while this browser is busy is turned away
    if (callRef.current) { call.hangup(); return; }
    callRef.current = call;
    const mine = (r: Ringing | null) => r?.call === call;
    call.on('active', () => setRinging((r) => (mine(r) ? { ...r!, answering: false, talking: true } : r)));
    call.on('ended', () => {
      if (callRef.current === call) callRef.current = null;
      setRinging((r) => (mine(r) ? null : r));
      setKept((k) => (k?.call === call ? { ...k, call: null, ended: true } : k));
    });
    setRinging({ call, answering: Boolean(out), talking: false, out });
    if (!out) return;
    if (audioRef.current) call.answer(audioRef.current);
    else call.hangup();
  }, []);

  /** Stop waiting for a call out's leg; anything that rang meanwhile rings as usual. */
  const release = useCallback((p: Placing) => {
    window.clearTimeout(p.timer);
    if (placingRef.current !== p) return;
    placingRef.current = null;
    setPlacing(false);
    const rest = p.parked;
    p.parked = [];
    for (const c of rest) show(c);
  }, [show]);

  const hear = useCallback((call: PhoneCall) => {
    const p = placingRef.current;
    if (p && call.callId) {
      // the server hasn't said yet which call is ours: hold it (a leg that ends meanwhile is let go)
      if (p.id === null) {
        p.parked.push(call);
        call.on('ended', () => { p.parked = p.parked.filter((c) => c !== call); });
        return;
      }
      // our own call out, by the id only this page was given: answered here once, then the wait is over
      if (call.callId === p.id) {
        const out = { number: p.number, id: p.id };
        p.id = '';
        show(call, out);
        release(p);
        return;
      }
    }
    show(call);
  }, [show, release]);

  const start = useCallback(() => {
    window.clearTimeout(retry.current.timer);
    phoneRef.current?.stop();
    setError(null);
    setGaveUp(false);
    phoneRef.current = startPhone((s, message, again) => {
      setState(s);
      if (s === 'connected') retry.current.tries = 0;
      if (s !== 'failed') return;
      setError(message ?? null);
      callRef.current?.hangup();
      callRef.current = null;
      setRinging(null);
      // a call cut off with the connection stays on the panel as ended, so its notes can be finished
      setKept((k) => (k && !k.ended ? { ...k, call: null, ended: true } : k));
      const wait = RETRY_SECONDS[retry.current.tries];
      if (again && wait !== undefined && wanted(userId)) {
        retry.current.tries += 1;
        retry.current.timer = window.setTimeout(start, wait * 1000);
      } else setGaveUp(true);
    }, hear);
  }, [hear, userId]);

  // a browser keeps sound off until the page is touched: unlock the ring on the first touch of each visit
  useEffect(() => (state === 'off' ? undefined : unlockRingOnTouch()), [state]);

  useEffect(() => {
    if (wanted(userId)) start();
    else setState('off');
    const r = retry.current;
    return () => {
      window.clearTimeout(r.timer);
      phoneRef.current?.stop();
      phoneRef.current = null;
    };
  }, [start, userId]);

  const turnOn = useCallback(() => {
    unlockRing();
    want(userId, true);
    retry.current.tries = 0;
    start();
  }, [start, userId]);

  const turnOff = useCallback(async () => {
    want(userId, false);
    window.clearTimeout(retry.current.timer);
    callRef.current?.hangup();
    callRef.current = null;
    setRinging(null);
    phoneRef.current?.stop();
    phoneRef.current = null;
    setBusy(true);
    setError(null);
    const r = await leavePhone();
    setBusy(false);
    setState('off');
    if (!r.ok) setError(r.message);
  }, [userId]);

  const callOut = useCallback(async (number: string, place: () => Promise<string | null>) => {
    if (callRef.current || placingRef.current) return false;
    const p: Placing = { number, id: null, parked: [], timer: 0 };
    placingRef.current = p;
    setPlacing(true);
    p.timer = window.setTimeout(() => release(p), OUT_WAIT);
    const id = await place().catch(() => null);
    if (placingRef.current !== p) return id !== null;
    if (!id) { release(p); return false; }
    p.id = id.toLowerCase();
    const ours = p.parked.find((c) => c.callId === p.id);
    if (ours) {
      p.parked = p.parked.filter((c) => c !== ours);
      p.id = '';
      show(ours, { number, id });
      release(p);
    }
    return true;
  }, [release, show]);

  // the panel: our call out from the moment it is placed, a call in once it is answered here; an ended one until Close
  const live = useMemo<PanelCall | null>(() => (ringing && (ringing.out || ringing.talking) ? {
    call: ringing.call, id: idOf(ringing), out: Boolean(ringing.out), talking: ringing.talking, ended: false,
    number: ringing.out?.number ?? ringing.call.number ?? null, name: ringing.out ? null : nameOf(ringing.call),
  } : null), [ringing]);
  useEffect(() => { if (live?.id) setKept(live); }, [live]);
  const panel = live ?? (kept?.ended ? kept : null);

  const onCall = placing || ringing !== null;
  const value = useMemo(() => ({ state, error, busy, turnOn, turnOff, onCall, callOut }), [state, error, busy, turnOn, turnOff, onCall, callOut]);
  const end = () => {
    const call = callRef.current;
    call?.hangup();
    callRef.current = null;
    setRinging(null);
    setKept((k) => (k && k.call === call ? { ...k, call: null, ended: true } : k));
  };
  const dock = panel && (
    <CallPanel key={panel.id ?? 'call'} pc={panel} audio={audioRef.current} onHangup={end} onBook={setBooking}
      onClose={() => setKept((k) => (k === panel ? null : k))} />
  );
  return (
    <CallsContext.Provider value={value}>
      <DockContext.Provider value={dock}>
      {children}
      </DockContext.Provider>
      <audio ref={audioRef} autoPlay hidden data-testid="call-audio" />
      {ringing && !live ? (
        <InBanner ringing={ringing} onEnd={end} onAnswer={() => {
          if (!audioRef.current) return;
          setRinging({ ...ringing, answering: true });
          ringing.call.answer(audioRef.current);
        }} />
      ) : !ringing && !onCallsPage && error !== null && (state === 'failed' ? gaveUp && wanted(userId) : state === 'off') && (
        <div role="status" data-testid="calls-lost"
          className="fixed inset-x-0 bottom-0 z-toast flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-line bg-popover px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-sm text-ink elevate lg:inset-x-auto lg:bottom-6 lg:right-6 lg:max-w-sm lg:rounded-card lg:border-0 lg:pb-3">
          {/* turned off but the server still counts this browser: say so, and Turn off asks again */}
          <p className="min-w-0 flex-1">{state === 'off' ? error : 'Calls can’t ring in this browser right now.'}</p>
          <div className="flex items-center gap-2">
            {state === 'off' ? null : <Button size="dense" className="max-sm:min-h-11" onClick={turnOn} data-testid="calls-lost-retry">Try again</Button>}
            <Button size="dense" variant="ghost" className="max-sm:min-h-11" disabled={busy} onClick={() => void turnOff()} data-testid="calls-lost-off">Turn off</Button>
            <InlineReport message={error} />
          </div>
        </div>
      )}
      <BookingDialog callId={booking} open={booking !== null} onClose={() => setBooking(null)} />
    </CallsContext.Provider>
  );
}

/** The id of the call on the panel: our own call out's, or the ringing leg's own. */
const idOf = (r: Ringing) => (r.out?.id ?? r.call.callId)?.toLowerCase() ?? null;
const digitsOf = (v?: string) => (v ?? '').replace(/\D/g, '');
/** The name the network gave a call in, when it is a name and not the number again. */
const nameOf = (call: PhoneCall) => (call.name && digitsOf(call.name) !== digitsOf(call.number) ? call.name : null);

/** The call panel, where the page puts it (AppShell: between the navigation and the page). */
const DockContext = createContext<ReactNode>(null);
export function CallDock() {
  return <>{useContext(DockContext)}</>;
}

/** A call ringing this browser: who it is, Answer and Decline. Once answered, the call panel takes over. */
function InBanner({ ringing, onAnswer, onEnd }: { ringing: Ringing; onAnswer(): void; onEnd(): void }) {
  const { call, answering } = ringing;
  useEffect(() => (answering ? undefined : startRing()), [answering]);
  // a call to an event's line reaches this browser from the staff caller ID, so who rang and which event come from our own
  // record of the call; a call without one shows what the call itself says
  const [from, setFrom] = useState<{ number: string | null; event: string | null } | null | undefined>(call.callId ? undefined : null);
  useEffect(() => {
    if (!call.callId) return;
    let live = true;
    void callFrom(call.callId).then((f) => { if (live) setFrom(f); }, () => { if (live) setFrom(null); });
    return () => { live = false; };
  }, [call.callId]);
  const name = nameOf(call);
  const who = call.callId
    ? from === undefined ? 'Finding the caller…' : [from?.number ? phone(from.number) : null, from?.event].filter(Boolean).join(' · ') || 'Unknown caller'
    : [name, call.number ? phone(call.number) : null].filter(Boolean).join(' · ') || 'Unknown caller';
  return (
    <div role="alertdialog" aria-labelledby="call-banner-title" aria-describedby="call-banner-from" data-testid="call-banner"
      className="fixed inset-x-0 bottom-0 z-toast flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-line bg-popover px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] elevate animate-rise lg:inset-x-auto lg:bottom-6 lg:right-6 lg:w-96 lg:rounded-card lg:border-0 lg:pb-3">
      <div className="min-w-0 flex-1">
        <p id="call-banner-title" className="text-sm font-semibold text-ink">Incoming call</p>
        <p id="call-banner-from" className="break-words text-sm text-ink-2" data-testid="call-banner-from">{who}</p>
      </div>
      <div className="flex gap-2">
        <Button variant="primary" className="max-sm:min-h-11" disabled={answering} onClick={onAnswer} data-testid="call-answer">
          {answering ? 'Answering…' : 'Answer'}
        </Button>
        <Button className="max-sm:min-h-11" onClick={onEnd} data-testid="call-decline">Decline</Button>
      </div>
    </div>
  );
}
